import {
  Button,
  IconButton,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { isQueryError, isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import { RedactConversationMessageApiArg } from 'lib-shared-types-api';
import { useEffect, useRef, useState } from 'react';

import { conversationsApi } from 'apis/conversations';
import { appActions } from 'app/app.actions';
import { prepareAttachments, prepareInlineImages } from 'app/utils/attachments';
import markdownToHTML from 'app/utils/markdownToHTML';
import { AttachedFile } from 'components/conversation/components/messages/utils/AttachedFile';
import { SafeInnerHtmlSetter } from 'components/shared/util/SafeInnerHtmlSetter';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import styles from './redactMessageModal.module.scss';
import {
  File,
  prepareFilesForUpload,
  redactedCharacter,
  RedactedTextNode,
  redactTextNodes,
  replaceSelectedText,
  restoreRedactedTextNodes,
  toggleFileDeletion,
} from './redactMessageModal.utils';

export const RedactMessageModal = createAppModal(
  {
    type: 'conversation:redactMessage',
    featureFlag: 'redact-messages',
  },

  ({
    isOpen,
    modal: {
      arg: { content, csid, messageId, images, attachments },
    },
    hide,
  }): JSX.Element => {
    const [isRedacted, setIsRedacted] = useState<boolean>(false);
    const [selectedCount, setSelectedCount] = useState<number>(0);
    const [inlineImages, setInlineImages] = useState<File[]>(
      prepareInlineImages(images),
    );
    const [attachedFiles, setAttachedFiles] = useState<File[]>(
      prepareAttachments(attachments),
    );
    const [redactedTextNodesHistory, setRedactedTextNodesHistory] = useState<
      RedactedTextNode[][]
    >([]);
    const [redactedTextNodesHitstoryIndex, setRedactedTextNodesHistoryIndex] =
      useState<number>(0);
    const [redactMessage, { isLoading }] =
      conversationsApi.useRedactMessageMutation();
    const dispatch = useAppDispatch();
    const contentRef = useRef<HTMLDivElement>(null);
    const notify = useNotifications();

    // Listen to selection changes
    useEffect(() => {
      const handleSelectionChange = () => {
        const selection = globalThis.getSelection();

        if (!selection || selection.rangeCount === 0) {
          setSelectedCount(0);
          return;
        }

        const range = selection.getRangeAt(0);

        // Check if selection is within our content div
        if (!contentRef.current?.contains(range.commonAncestorContainer)) {
          setSelectedCount(0);
          return;
        }

        const selectedText = selection
          .toString()
          .replaceAll(redactedCharacter, '');
        setSelectedCount(selectedText.length);
      };

      document.addEventListener('selectionchange', handleSelectionChange);

      return () => {
        document.removeEventListener('selectionchange', handleSelectionChange);
      };
    }, []);

    const saveRedactedTextNodesInHistory = (
      redactedTextNodes: RedactedTextNode[],
    ) => {
      setRedactedTextNodesHistory((currentHistory) => {
        const newHistory = [
          ...(redactedTextNodesHitstoryIndex !== redactedTextNodesHistory.length
            ? redactedTextNodesHistory.slice(0, redactedTextNodesHitstoryIndex)
            : currentHistory),
          redactedTextNodes,
        ];
        setRedactedTextNodesHistoryIndex(newHistory.length);
        return newHistory;
      });
    };

    const confirmRedaction = async () => {
      const text =
        redactedTextNodesHitstoryIndex > 0 ? contentRef.current?.innerHTML : '';
      const { isConfirmed } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'app:confirm',
            arg: {
              title: 'Redact message?',
              description: `Are you sure you want to redact this message? Please note that this action can't be undone. By redacting a message you are creating a copy of the old message with the redacted content, and anonymizing the old one.`,
              confirm: {
                label: 'Confirm',
              },
            },
          },
        }),
      );
      if (!isConfirmed) {
        return;
      }
      ampli.conversationMessageRedactionModalSubmitted();
      const textOverrides: RedactConversationMessageApiArg['attributeOverrides'] =
        content && content.length > 0 && text && text.length > 0
          ? {
              text,
              content_type: 'Html',
            }
          : {};
      const inlineImagesOverrides: RedactConversationMessageApiArg['attributeOverrides'] =
        inlineImages.length > 0
          ? {
              inline_images_v2: JSON.stringify(
                prepareFilesForUpload(inlineImages),
              ),
            }
          : {};
      const attachedFilesOverrides: RedactConversationMessageApiArg['attributeOverrides'] =
        attachedFiles.length > 0
          ? {
              attached_files_v2: JSON.stringify(
                prepareFilesForUpload(attachedFiles),
              ),
            }
          : {};
      const response = await redactMessage({
        csid,
        messageId,
        attributeOverrides: {
          ...textOverrides,
          ...inlineImagesOverrides,
          ...attachedFilesOverrides,
        },
      });
      handleResponse(response);
    };

    const handleResponse = (
      response: Awaited<ReturnType<typeof redactMessage>>,
    ) => {
      if (!isResponseWithData(response) && isQueryError(response.error)) {
        if (response.error.status === 400) {
          notify(
            {
              title: 'Message not inbound!',
              text: 'Only inbound messaged can be redacted!',
              time: 5000,
            },
            NotificationType.Error,
          );
        }
        if (response.error.status === 404) {
          notify(
            {
              title: 'Message not found!',
              text: 'Please try again!',
              time: 5000,
            },
            NotificationType.Error,
          );
        } else if (response.error.status === 409) {
          notify(
            {
              title: 'Message not anonymized!',
              text: 'There was an error when anonymizing the old message. Please anonymize it manually!',
              time: 5000,
            },
            NotificationType.Error,
          );
        } else if (response.error.status !== 403) {
          notify(
            {
              title: 'Server error!',
              text: 'Please try again!',
              time: 5000,
            },
            NotificationType.Error,
          );
        }
      } else {
        hide();
        notify(
          {
            title: 'Message redacted!',
            text: 'The message was successfully redacted.',
            time: 5000,
          },
          NotificationType.Success,
        );
      }
    };

    const onUndoClick = () => {
      restoreRedactedTextNodes(
        redactedTextNodesHistory[redactedTextNodesHitstoryIndex - 1],
      );
      setRedactedTextNodesHistoryIndex(redactedTextNodesHitstoryIndex - 1);
    };

    const onRedoClick = () => {
      redactTextNodes(redactedTextNodesHistory[redactedTextNodesHitstoryIndex]);
      setRedactedTextNodesHistoryIndex(redactedTextNodesHitstoryIndex + 1);
    };
    const onRedactClick = () => {
      const redactedTextNodes = replaceSelectedText(contentRef.current);
      setIsRedacted(true);
      redactedTextNodes.length > 0 &&
        saveRedactedTextNodesInHistory(redactedTextNodes);
    };
    const onImageDeletionClick = (url: string) => {
      setInlineImages((currentImages) =>
        toggleFileDeletion(currentImages, url),
      );
      setIsRedacted(true);
    };

    const onAttachementDeletionClick = (url: string) => {
      setAttachedFiles((currentAttachments) =>
        toggleFileDeletion(currentAttachments, url),
      );
      setIsRedacted(true);
    };

    return (
      <Modal isOpen={isOpen} onClose={hide} allowClickOutside={!isLoading}>
        <ModalHead
          title="Redact message"
          onClose={isLoading ? undefined : hide}
        />
        <ModalBody>
          {content && content.length > 0 && (
            <>
              <Text.Paragraph>
                Select the text you want to redact from the `Redacted:` box, and
                click on the Redact button. This can be done many times.
              </Text.Paragraph>
              <div className={styles.buttons}>
                <IconButton
                  icon="undo"
                  variant="secondary"
                  disabled={isLoading || redactedTextNodesHitstoryIndex === 0}
                  onClick={onUndoClick}
                />
                <IconButton
                  icon="redo"
                  variant="secondary"
                  disabled={
                    isLoading ||
                    redactedTextNodesHitstoryIndex ===
                      redactedTextNodesHistory.length
                  }
                  onClick={onRedoClick}
                />
                <Button
                  onClick={onRedactClick}
                  disabled={isLoading || selectedCount === 0}
                >
                  {selectedCount == 0
                    ? 'Redact'
                    : `Redact (${selectedCount} characters)`}
                </Button>
              </div>
              <div className={styles.diffContainer}>
                <div className={styles.diffContainerTitle}>Redacted:</div>
                <div className={styles.diffContainerTitle}>Original:</div>
                {/* Redacted */}
                <SafeInnerHtmlSetter
                  element="div"
                  innerRef={contentRef}
                  content={markdownToHTML(content)}
                  className={styles.redactableContent}
                  noSanitize
                />
                {/* Original */}
                <SafeInnerHtmlSetter
                  element="div"
                  content={markdownToHTML(content)}
                  className={styles.originalContent}
                  noSanitize
                />
              </div>
            </>
          )}
          {(attachedFiles.length > 0 || inlineImages.length > 0) && (
            <>
              <Text.Paragraph>
                You can remove attachments or images by clicking on the trash
                icon. This will mark the file for deletion. To unmark the file
                for deletion, click on the plus icon.
              </Text.Paragraph>
              <div className={styles.attachments}>
                {inlineImages.map((image) => (
                  <AttachedFile
                    key={image.relative_uri}
                    fileUrl={image.absolute_uri}
                    prettyName={image.name}
                    className={
                      image.markedForDeletion ? styles.markedForDeletion : ''
                    }
                    extraButtons={[
                      <IconButton
                        key={image.relative_uri}
                        variant="secondary"
                        icon={image.markedForDeletion ? 'plus' : 'trash'}
                        onClick={() => onImageDeletionClick(image.relative_uri)}
                        disabled={isLoading}
                      />,
                    ]}
                  />
                ))}
                {attachedFiles.map((attachment) => {
                  return (
                    <AttachedFile
                      key={attachment.relative_uri}
                      fileUrl={attachment.absolute_uri}
                      prettyName={attachment.name}
                      className={
                        attachment.markedForDeletion
                          ? styles.markedForDeletion
                          : ''
                      }
                      extraButtons={[
                        <IconButton
                          key={attachment.relative_uri}
                          variant="secondary"
                          icon={attachment.markedForDeletion ? 'plus' : 'trash'}
                          onClick={() =>
                            onAttachementDeletionClick(attachment.relative_uri)
                          }
                          disabled={isLoading}
                        />,
                      ]}
                    />
                  );
                })}
              </div>
            </>
          )}
          <Text.Paragraph>
            Once you are done redacting, click Confirm.
          </Text.Paragraph>
        </ModalBody>
        <ModalFoot
          confirmOnClick={confirmRedaction}
          confirmName="Confirm"
          cancelOnClick={hide}
          cancelName="Cancel"
          confirmProps={{
            disabled:
              isLoading ||
              !isRedacted ||
              (redactedTextNodesHitstoryIndex === 0 &&
                inlineImages.every((image) => !image.markedForDeletion) &&
                attachedFiles.every(
                  (attachment) => !attachment.markedForDeletion,
                )),
            isLoading,
          }}
          cancelProps={{
            disabled: isLoading,
          }}
        />
      </Modal>
    );
  },
);
